/* global Stripe */

const StripePort = {
  add: function (app, publishableKey) {
    if (!window.Stripe) {
      return;
    }

    let stripeElements = {};

    // Cross browser support
    const requestAnimationFrame =
      window.requestAnimationFrame ||
      window.mozRequestAnimationFrame ||
      window.webkitRequestAnimationFrame ||
      window.msRequestAnimationFrame;

    const mountAttemptsMax = 20;
    let mountAttempts = 0;

    function buildAndMountElementWithSetupIntent(stripeClient, params) {
      const cardElement = document.querySelector(params.selector);

      const elements = stripeClient.elements({
        clientSecret: params.setupIntentSecret,
        fonts: [
          {
            cssSrc:
              "https://fonts.googleapis.com/css2?family=Wix+Madefor+Text:ital,wght@0,400..800;1,400..800&display=swap",
          },
        ],
        appearance: {
          theme: "flat",
          variables: {
            borderRadius: "3px",
            border: "1px solid #C4DCF8",
            boxShadow: "none",
            colorPrimary: "#006CD1",
            colorText: "#1D2334",
            colorTextPlaceholder: "#497AA5",
            colorTextSecondary: "#497AA5",
            fontFamily: "Wix Madefor Text",
            fontLineHeight: "21px",
            colorBackground: "#FFFFFF",
            fontSize: "1rem",
            colorIconTabSelected: "#0056A6",
            colorIconTabHover: "#0056A6",
          },
          rules: {
            ".DropdownItem--highlight": {
              backgroundColor: "#F1F6FC",
              border: "1px solid #81AFE4",
            },
            ".Tab--selected, .Tab--selected:hover": {
              borderColor: "#0056A6",
              color: "#0056A6",
              backgroundColor: "#f1f6fc",
            },
            ".Tab, .Input, .PickerItem": {
              border: "1px solid #c4dcf8",
              boxShadow: "none",
            },
            ".Input": {
              padding: "10px",
              fontSize: "1rem",
            },
            ".Label": {
              fontWeight: "850",
              fontSize: "1rem",
              fontLineHeight: "21px",
              marginBottom: "10px",
            },
            ".PickerItem": {
              color: "#497aa4",
              textTransform: "uppercase",
              lineHeight: "25px",
            },
            ".PickerItem:hover": {
              backgroundColor: "#FFFFFF",
            },
            ".Tab:hover, .Input:hover, .PickerItem:hover": {
              borderColor: "#81AFE4",
              color: "#0056A6",
            },
          },
        },
      });
      const disallowedBrands = params.options.rejectAmex ? ["amex"] : [];
      const paymentMethodOrder = params.options.encourageAch ? ["us_bank_account", "card"] : [];
      const paymentElement = elements.create("payment", {
        paymentMethodOrder,
        terms: {
          card: "never",
          usBankAccount: "never",
        },
        disallowedCardBrands: disallowedBrands,
        wallets: {
          applePay: params.options.applePay ? "auto" : "never",
          googlePay: "never",
        },
      });

      paymentElement.mount(cardElement);
      paymentElement.on("change", function (element) {
        const type = (element && element.value && element.value.type) || "";
        app.ports.stripePortOnSetupIntentChange.send(type);
      });

      return {
        stripeClient: stripeClient,
        elements: elements,
        paymentElement: paymentElement,
        options: params.options,
      };
    }

    function tryBuildAndMountElementWithSetupIntent(params) {
      const clientOptions = {
        betas: ["blocked_card_brands_beta_1"],
        apiVersion: "2019-12-03",
        locale: params.locale,
      };
      if (params.stripeAccount) {
        clientOptions.stripeAccount = params.stripeAccount;
      }
      const stripeClient = Stripe(publishableKey, clientOptions);
      const cardElement = document.querySelector(params.selector);

      if (cardElement) {
        requestAnimationFrame(function () {
          stripeElements[params.selector] = buildAndMountElementWithSetupIntent(stripeClient, params);
        });
      } else {
        if (mountAttempts <= mountAttemptsMax) {
          mountAttempts += 1;
          setTimeout(function () {
            tryBuildAndMountElementWithSetupIntent(params);
          }, 100);
        }
      }
    }

    app.ports.stripePortBuildAndMountElementWithSetupIntent.subscribe(tryBuildAndMountElementWithSetupIntent);

    // ---- Apple Pay express checkout ----
    // A fresh Stripe client: the Express Checkout Element is not available
    // under the pinned legacy apiVersion the card form uses. The payment lives
    // on the practice's connected account, so the client is scoped to it.
    let walletCheckout = null;

    function unmountExpressCheckout() {
      if (walletCheckout && walletCheckout.expressCheckout) {
        walletCheckout.expressCheckout.unmount();
      }
      walletCheckout = null;
    }

    function mountExpressCheckout(params, attempt) {
      unmountExpressCheckout();

      const container = document.querySelector(params.selector);

      if (!container) {
        if ((attempt || 0) <= mountAttemptsMax) {
          setTimeout(function () {
            mountExpressCheckout(params, (attempt || 0) + 1);
          }, 100);
        }
        return;
      }

      const clientOptions = params.stripeAccount
        ? { locale: params.locale, stripeAccount: params.stripeAccount }
        : { locale: params.locale };
      const stripeClient = Stripe(publishableKey, clientOptions);
      const elements = stripeClient.elements({ clientSecret: params.clientSecret });
      const expressCheckout = elements.create("expressCheckout", {
        paymentMethods: {
          applePay: "always",
          googlePay: "never",
          link: "never",
          amazonPay: "never",
          paypal: "never",
        },
        buttonType: { applePay: "plain" },
        buttonTheme: { applePay: "black" },
        buttonHeight: 48,
      });

      expressCheckout.on("ready", function (event) {
        const available = !!(event && event.availablePaymentMethods && event.availablePaymentMethods.applePay);
        app.ports.stripePortExpressCheckoutReady.send(available);
      });
      expressCheckout.on("confirm", function () {
        app.ports.stripePortExpressCheckoutConfirmed.send(null);
      });
      expressCheckout.on("cancel", function () {
        app.ports.stripePortWalletPaymentResult.send({ succeeded: false, cancelled: true, error: null });
      });

      expressCheckout.mount(params.selector);
      walletCheckout = { stripeClient: stripeClient, elements: elements, expressCheckout: expressCheckout };
    }

    app.ports.stripePortMountExpressCheckout.subscribe(function (params) {
      requestAnimationFrame(function () {
        mountExpressCheckout(params, 0);
      });
    });

    app.ports.stripePortUnmountExpressCheckout.subscribe(unmountExpressCheckout);

    app.ports.stripePortConfirmWalletPayment.subscribe(function (clientSecret) {
      if (!walletCheckout) {
        app.ports.stripePortWalletPaymentResult.send({ succeeded: false, cancelled: false, error: null });
        return;
      }

      walletCheckout.stripeClient
        .confirmPayment({
          elements: walletCheckout.elements,
          clientSecret: clientSecret,
          confirmParams: { return_url: window.location.href },
          redirect: "if_required",
        })
        .then(function (result) {
          const intent = result.paymentIntent;
          const succeeded =
            !result.error && intent && (intent.status === "requires_capture" || intent.status === "succeeded");
          app.ports.stripePortWalletPaymentResult.send({
            succeeded: !!succeeded,
            cancelled: false,
            error: result.error ? result.error.message : null,
          });
        })
        .catch(function () {
          app.ports.stripePortWalletPaymentResult.send({ succeeded: false, cancelled: false, error: null });
        });
    });

    app.ports.stripePortConfirmSetupIntent.subscribe(function (params) {
      const stripeElement = stripeElements[params.selector];
      const billingDetails =
        params.options && params.options.billingOptions
          ? {
              billing_details: {
                name: params.options.billingOptions.name,
              },
            }
          : {};

      stripeElement.stripeClient
        .confirmSetup({
          elements: stripeElement.elements,
          confirmParams: {
            return_url: window.location.href, // handle a return url here for 3d secure cards
            payment_method_data: billingDetails,
          },
          redirect: "if_required", // only redirect if needed for 3d secure
        })
        .then(({ error, setupIntent }) => {
          if (error) {
            app.ports.stripePortOnSetupIntentConfirmed.send({
              selector: params.selector,
              setup_intent: null,
              error: error.message,
            });
          } else {
            app.ports.stripePortOnSetupIntentConfirmed.send({
              selector: params.selector,
              setup_intent: setupIntent,
              error: null,
            });
          }
        });
    });
  },
};

module.exports = StripePort;
